/**
 * Shared error normalizer for the API helpers in src/helpers/*Api.js.
 *
 * Produces the shape callers already rely on ({message, status, details}) plus
 * `detail`: the backend's structured {code, params, message} payload (the
 * server.* i18n convention) when the response carries one, so views can
 * translate errors client-side via translateServerMessage/translateApiError
 * (see src/utils/serverMessages.js).
 *
 * Keeps `message` a readable string even when the backend sends a dict detail
 * (never "[object Object]"), so a caller that only reads `.message` still
 * shows the English fallback rather than a broken string.
 *
 * @param {Object} error axios error
 * @param {string} serverFallback shown when the response carries no message
 * @param {string} [networkFallback] shown when the request never reached the server
 * @returns {{message: string, status: number, details: *, detail?: Object|null}}
 */
export function wrapApiError(error, serverFallback, networkFallback) {
  if (error.response) {
    const data = error.response.data || {};
    const structured = data.detail && typeof data.detail === 'object' ? data.detail : null;
    return {
      message: (structured && structured.message)
        || (typeof data.detail === 'string' ? data.detail : null)
        || data.message
        || serverFallback,
      status: error.response.status,
      details: data,
      detail: structured,
      // Compatibility bridge: some older call sites still dig into
      // error.response.data directly; keep it reachable so helpers can be
      // migrated to wrapApiError without breaking those readers.
      response: error.response,
    };
  }
  return {
    message: networkFallback || serverFallback,
    status: 0,
    details: error.request || error.message,
  };
}
